'use client';

import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import type Experiments from '@alltrails/core/types/Experiments';
// eslint-disable-next-line @alltrails/custom/react-imports
import React, { Fragment, PropsWithChildren } from 'react';
import { MapProvider, MapState } from '@alltrails/maps';
import { createIntl } from '@formatjs/intl';
import type { Bootstrap } from '@alltrails/networking/types/Bootstrap';
import setGlobalFetchHeaders from '@alltrails/core/utils/setGlobalFetchHeaders';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Bugsnag from '@bugsnag/js';
import { startClientBugsnag } from '@alltrails/stability';
import type { userAgent } from 'next/server';
import { buildStore } from '../utils/redux';

type Props = {
  bootstrap: Bootstrap;
  browser: ReturnType<typeof userAgent>['browser'];
  experiments: Experiments;
  messages: ReturnType<typeof createIntl>['messages'];
  locale: ReturnType<typeof createIntl>['locale'];
  irclickid?: string;
  map: MapState;
};

startClientBugsnag();
const plugin = Bugsnag.getPlugin('react');
const ErrorBoundary = plugin ? plugin.createErrorBoundary(React) : Fragment;

export default function CustomProvider({ bootstrap, browser, experiments, messages, locale, children, irclickid, map }: PropsWithChildren<Props>) {
  const languageRegionCode = useLanguageRegionCode();
  setGlobalFetchHeaders({ languageRegionCode });
  const store = buildStore({
    algoliaConfigs: bootstrap.algoliaConfig,
    amplitudeAnalyticsSettings: bootstrap.amplitudeAnalyticsSettings,
    browser,
    context: bootstrap.context,
    currencyCode: bootstrap.currencyCode,
    experiments,
    features: bootstrap.features,
    irclickid,
    plans: bootstrap.plans,
    requestCountryCode: bootstrap.requestCountryCode,
    map
  });

  return (
    <ErrorBoundary>
      <IntlProvider messages={messages} locale={locale}>
        <Provider store={store}>
          <MapProvider>{children}</MapProvider>
        </Provider>
      </IntlProvider>
    </ErrorBoundary>
  );
}
